import {Button, Form, Spinner, Stack} from "react-bootstrap";
import {ChangePasswordDialogContext, FormRowControl, SaveButton} from "zavadil-react-common";
import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {EmailUtil, StringUtil} from "zavadil-ts-common";
import {useNavigate, useParams} from "react-router";
import {User, UserData} from "../../../types/User";
import {useRestClient} from "../../../client/SpmRestClient";
import SelectRoles from "../../../components/SelectRoles";
import {useAlerts} from "../../../utils/UserAlerts";
import BackIconLink from "../../../components/BackIconLink";

export default function UserPage() {
	const {id} = useParams();
	const restClient = useRestClient();
	const userAlers = useAlerts();
	const navigate = useNavigate();
	const changePasswordDialog = useContext(ChangePasswordDialogContext);
	const [user, setUser] = useState<User>();
	const [roleIds, setRoleIds] = useState<Array<number>>();
	const [saving, setSaving] = useState<boolean>(false);
	const [changed, setChanged] = useState<boolean>(false);

	const nameValid = useMemo(() => user && StringUtil.notBlank(user.name), [user]);
	const emailValid = useMemo(() => user && EmailUtil.isValidEmail(user.email), [user]);
	const formValid = useMemo(() => changed && nameValid && emailValid, [changed, nameValid, emailValid]);

	const onChanged = useCallback(
		() => {
			if (user) setUser({...user});
			setChanged(true);
		},
		[user]
	);

	const save = useCallback(
		() => {
			if (!(user && roleIds)) return;
			setSaving(true);
			const inserting = !user.id;
			const data: UserData = {
				id: user.id,
				name: user.name,
				email: user.email,
				roles: roleIds
			}
			restClient.users.save(data)
				.then((u) => {
					userAlers.info("Uživatel byl uložen");
					if (inserting) {
						navigate(`/admin/user/${u.id}`);
					} else {
						setUser(u);
						setChanged(false);
					}
				})
				.catch((e) => userAlers.err(e))
				.finally(() => setSaving(false));
		},
		[restClient, user, roleIds, userAlers, navigate]
	);

	useEffect(
		() => {
			if (StringUtil.isBlank(id)) {
				setUser(
					{
						id: null,
						name: '',
						email: '',
						roles: []
					}
				);
				setRoleIds([]);
				return;
			}

			restClient.users.load(Number(id)).then(
				(u: User) => {
					setUser(u);
					setRoleIds(u.roles.map(r => Number(r.id)));
				}
			);
		},
		[]
	);

	const changePassword = useCallback(
		() => {
			if (!user?.id) return;
			changePasswordDialog.show(
				{
					name: 'Změnit heslo',
					onClose: () => changePasswordDialog.hide(),
					onConfirm: (password) => {
						restClient.users
							.changePassword(Number(user.id), password)
							.catch((e) => userAlers.err(e))
							.then(() => {
								userAlers.info("Heslo bylo změněno");
								changePasswordDialog.hide();
							});
					}
				}
			)
		},
		[changePasswordDialog, restClient, userAlers, user]
	);

	if (!(user && roleIds)) return <Spinner/>

	return (
		<div>
			<h1>Uživatel</h1>
			<Form onSubmit={save}>
				<Stack direction="vertical" gap={2}>
					<Stack direction="horizontal">
						<BackIconLink/>
					</Stack>
					<div>
						<FormRowControl
							label="Jméno"
							type="text"
							disabled={saving}
							minLength={1}
							maxLength={255}
							value={user.name}
							onChange={(e) => {
								user.name = e.target.value;
								onChanged();
							}}
						/>
						{
							nameValid === false && <small className="error">Vyplňte jméno uživatele</small>
						}
					</div>

					<div>
						<FormRowControl
							label="Email"
							type="email"
							disabled={saving}
							minLength={1}
							maxLength={255}
							value={user.email}
							onChange={(e) => {
								user.email = e.target.value;
								onChanged();
							}}
						/>
						{
							emailValid === false && <small className="error">Vyplňte platný email</small>
						}
					</div>

					<div>
						<div>Role</div>
						<SelectRoles
							selectedRoleIds={roleIds}
							onChange={
								(ids) => {
									setRoleIds(ids);
									setChanged(true);
								}
							}
						/>
					</div>
				</Stack>

				<Stack direction="horizontal" gap={2} className="mt-2">
					<SaveButton loading={saving} onClick={save} disabled={!formValid}>Uložit</SaveButton>
					{
						user.id && <Button onClick={changePassword}>Změnit heslo...</Button>
					}
				</Stack>
			</Form>

		</div>
	);
}
